import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../lib/auth';
import { DemoBar } from './DemoBar';
import { Icon } from './icons';
import { LanguageSwitch } from './ui';

type Item = readonly [to: string, key: string, icon: string];

/** Full navigation per role (desktop top bar) */
const NAV: Record<'staff' | 'admin' | 'guardian', readonly Item[]> = {
  staff: [
    ['/', 'nav.dismissal', 'gate'],
    ['/students', 'nav.students', 'students'],
    ['/messages', 'nav.messages', 'messages'],
    ['/reports', 'nav.reports', 'reports'],
  ],
  admin: [
    ['/', 'nav.dismissal', 'gate'],
    ['/students', 'nav.students', 'students'],
    ['/users', 'nav.users', 'users'],
    ['/messages', 'nav.messages', 'messages'],
    ['/reports', 'nav.reports', 'reports'],
    ['/audit', 'nav.audit', 'audit'],
    ['/settings', 'nav.settings', 'settings'],
  ],
  guardian: [
    ['/', 'nav.today', 'today'],
    ['/people', 'nav.people', 'people'],
    ['/changes', 'nav.changes', 'changes'],
    ['/history', 'nav.history', 'history'],
  ],
};

/** Phone tab bar: up to 4 destinations + Account (or "More" when a role has more pages) */
const TABS: Record<'staff' | 'admin' | 'guardian', { tabs: readonly Item[]; more: readonly Item[] }> = {
  guardian: { tabs: [NAV.guardian[0], ['/people', 'nav.peopleShort', 'people'], NAV.guardian[2], NAV.guardian[3], ['/account', 'nav.account', 'account']], more: [] },
  staff: { tabs: [...NAV.staff, ['/account', 'nav.account', 'account']], more: [] },
  admin: {
    tabs: [NAV.admin[0], NAV.admin[1], NAV.admin[2], NAV.admin[3]],
    more: [NAV.admin[4], NAV.admin[5], NAV.admin[6], ['/account', 'nav.account', 'account']],
  },
};

export function Mark() {
  return (
    <svg className="mark" viewBox="0 0 64 64" aria-hidden="true">
      <rect x="14" y="14" width="36" height="36" rx="6" transform="rotate(45 32 32)" />
      <path d="M24 33l6 6 11-13" />
    </svg>
  );
}

export function Layout() {
  const { t, i18n } = useTranslation();
  const { user, school, logout, setLocale } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMoreOpen(false), [location.pathname]);
  if (!user) return null;
  const schoolName = i18n.language === 'ar' && school?.nameAr ? school.nameAr : school?.name;
  const { tabs, more } = TABS[user.role];
  const moreActive = more.some(([to]) => location.pathname.startsWith(to));

  return (
    <div className={`shell${school?.isDemo ? ' is-demo' : ''}`}>
      <DemoBar />
      <header className="topbar">
        <div className="brand">
          {school?.logoUrl ? <img className="brand-logo" src={school.logoUrl} alt="" /> : <Mark />}
          <div>
            <strong>{t('app.name')}</strong>
            <span className="brand-school">{schoolName}</span>
          </div>
        </div>
        <nav className="mainnav" aria-label={t('nav.main')}>
          {NAV[user.role].map(([to, key]) => (
            <NavLink key={to} to={to} end={to === '/'}>
              {t(key)}
            </NavLink>
          ))}
        </nav>
        <div className="topbar-end">
          <LanguageSwitch onChange={(l) => void setLocale(l)} />
          <NavLink to="/account" className="account-link" title={t('nav.account')}>
            {user.fullName}
          </NavLink>
          <button type="button" className="btn-quiet signout-top" onClick={() => void logout()}>
            {t('auth.signOut')}
          </button>
        </div>
      </header>

      <main className="content">
        <Outlet />
      </main>

      {/* Phones: app-style tab bar */}
      <nav className="tabbar" aria-label={t('nav.main')}>
        {tabs.map(([to, key, icon]) => (
          <NavLink key={to} to={to} end={to === '/'} className="tab">
            <Icon name={icon} />
            <span>{t(key)}</span>
          </NavLink>
        ))}
        {more.length > 0 && (
          <button type="button" className={`tab${moreActive || moreOpen ? ' active' : ''}`} aria-expanded={moreOpen} onClick={() => setMoreOpen((o) => !o)}>
            <Icon name="more" />
            <span>{t('nav.more')}</span>
          </button>
        )}
      </nav>
      {moreOpen && (
        <div className="more-sheet" role="dialog" aria-label={t('nav.more')}>
          <button type="button" className="more-backdrop" aria-label={t('common.close')} onClick={() => setMoreOpen(false)} />
          <div className="more-panel">
            {more.map(([to, key, icon]) => (
              <NavLink key={to} to={to} className="more-item">
                <Icon name={icon} />
                <span>{t(key)}</span>
              </NavLink>
            ))}
            <button type="button" className="more-item" onClick={() => void logout()}>
              <Icon name="signout" />
              <span>{t('auth.signOut')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
